import React, { useCallback, useEffect, useRef, useState } from 'react';
import { squareAPI } from '../services/api';

/**
 * Standalone payment modal for processing payment on an existing service/ticket.
 *
 * Opened from:
 *  - Service detail panel ("Process payment" button when status is ready_for_billing / pending_payment)
 *  - SquarePendingOrders list
 *
 * Payment options:
 *  - Terminal: send to Square Terminal device
 *  - Cash: record cash payment in Square
 *  - Already paid: mark service as completed (payment was handled in Square POS)
 */
function SquarePaymentModal({ isOpen, onClose, onPaid, darkMode = true, paymentContext }) {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('terminal');
  const [processingPayment, setProcessingPayment] = useState(false);

  // Terminal state
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [checkoutId, setCheckoutId] = useState(null);
  const [terminalStatus, setTerminalStatus] = useState(null);
  const [terminalSentAt, setTerminalSentAt] = useState(null);
  const pollingRef = useRef(null);
  const autoSentRef = useRef(false);

  const orderId = paymentContext?.orderId;
  const serviceId = paymentContext?.serviceId;
  const amountCents = paymentContext?.amountCents || 0;
  const currency = paymentContext?.currency || 'GBP';
  const frameNumber = paymentContext?.frameNumber || '';
  const locationId = paymentContext?.locationId;
  const ticketName = paymentContext?.ticketName || '';

  // ── Load devices on open ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSuccess('');
    setCheckoutId(null);
    setTerminalStatus(null);
    setTerminalSentAt(null);
    autoSentRef.current = false;
    setPaymentMethod('terminal');

    const loadDevices = async () => {
      try {
        setLoadingDevices(true);
        const res = await squareAPI.listDevices(locationId);
        const deviceList = res?.data?.devices || [];
        setDevices(deviceList);
        if (deviceList.length > 0) {
          const preferred = deviceList.find(d =>
            ['Online', 'AVAILABLE', 'ACTIVE', 'ACTIVE_READY', 'PAIRED'].includes(d.status)
          ) || deviceList[0];
          setSelectedDeviceId(preferred.id);
        }
      } catch (err) {
        console.error('Failed to load devices', err);
      } finally {
        setLoadingDevices(false);
      }
    };
    loadDevices();
  }, [isOpen, locationId]);

  // Cleanup polling on unmount or close
  useEffect(() => {
    return () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
  }, []);

  // ── Polling ─────────────────────────────────────────────────
  const startPolling = useCallback((checkoutIdToCheck) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await squareAPI.getTerminalCheckout(checkoutIdToCheck);
        const status = res.data?.status;
        setTerminalStatus(status);
        if (status === 'COMPLETED') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setProcessingPayment(false);
          setSuccess('Payment completed!');
          if (onPaid) onPaid({ orderId: res.data?.order_id || orderId, serviceId, paymentMethod: 'terminal' });
          setTimeout(() => onClose(), 2500);
        } else if (status === 'CANCELED' || status === 'CANCEL_REQUESTED' || status === 'FAILED') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setProcessingPayment(false);
          setError(`Terminal payment ${status === 'FAILED' ? 'failed' : 'was cancelled'}. Try again or choose another method.`);
          setTerminalStatus(null);
          setCheckoutId(null);
          autoSentRef.current = false;
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  }, [orderId, serviceId, onPaid, onClose]);

  // ── Process payment ─────────────────────────────────────────
  const handleProcessPayment = useCallback(async () => {
    if (!orderId && amountCents <= 0) {
      setError('No order or amount to process.');
      return;
    }

    try {
      setError('');
      setProcessingPayment(true);

      if (paymentMethod === 'terminal') {
        if (!selectedDeviceId) {
          setError('Please select a terminal device.');
          setProcessingPayment(false);
          return;
        }

        const res = await squareAPI.createTerminalCheckout({
          order_id: orderId,
          device_id: selectedDeviceId,
          amount_cents: amountCents,
          currency,
          frame_number: frameNumber,
          location_id: locationId,
          note: ticketName ? `${ticketName} - Frame: ${frameNumber}` : `Frame: ${frameNumber}`,
        });

        const newCheckoutId = res.data?.checkout_id;
        if (newCheckoutId) {
          setCheckoutId(newCheckoutId);
          setTerminalStatus('PENDING');
          setTerminalSentAt(new Date());
          startPolling(newCheckoutId);
        } else {
          setProcessingPayment(false);
          setSuccess('Payment sent to terminal!');
          if (onPaid) onPaid({ orderId: res.data?.order_id || orderId, serviceId, paymentMethod: 'terminal' });
          setTimeout(() => onClose(), 3000);
        }
        return;

      } else if (paymentMethod === 'cash') {
        await squareAPI.createCashPayment({
          order_id: orderId,
          amount_cents: amountCents,
          currency,
          note: ticketName ? `${ticketName} - Cash` : `Frame: ${frameNumber} - Cash payment`,
        });
        setSuccess('Cash payment recorded!');
      } else if (paymentMethod === 'already_paid') {
        await squareAPI.markOrderPaid({
          order_id: orderId,
          service_id: serviceId,
        });
        setSuccess('Service marked as paid and completed.');
      }

      if (onPaid) onPaid({ orderId, serviceId, paymentMethod });
      setTimeout(() => onClose(), 2500);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || `Failed to process ${paymentMethod} payment`);
    } finally {
      if (paymentMethod !== 'terminal') setProcessingPayment(false);
    }
  }, [orderId, amountCents, paymentMethod, selectedDeviceId, currency, frameNumber, locationId, ticketName, serviceId, onPaid, onClose, startPolling]);

  // ── Auto-send to terminal ──────────────────────────────────
  useEffect(() => {
    if (
      isOpen &&
      paymentMethod === 'terminal' &&
      selectedDeviceId &&
      !processingPayment &&
      !checkoutId &&
      !autoSentRef.current &&
      !success &&
      (orderId || amountCents > 0)
    ) {
      autoSentRef.current = true;
      handleProcessPayment();
    }
  }, [isOpen, paymentMethod, selectedDeviceId, processingPayment, checkoutId, success, orderId, amountCents, handleProcessPayment]);

  if (!isOpen) return null;

  const amountDisplay = `${(amountCents / 100).toFixed(2)} ${currency}`;

  return (
    <div className="modal-overlay" onClick={() => !processingPayment && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', position: 'relative' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Process payment</h2>
          <button className="close-btn" onClick={onClose} disabled={processingPayment}>x</button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Order/service summary */}
          <div style={{
            marginBottom: '16px', padding: '14px', borderRadius: '10px',
            background: darkMode ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
            border: `1px solid ${darkMode ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)'}`,
          }}>
            <div style={{ fontWeight: 700, fontSize: '18px', color: '#16a34a' }}>
              {amountDisplay}
            </div>
            {ticketName && (
              <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', marginTop: '4px' }}>
                {ticketName}
              </div>
            )}
            {frameNumber && (
              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af', marginTop: '2px' }}>
                Frame: {frameNumber}
              </div>
            )}
          </div>

          {error && <div className="error" style={{ marginBottom: '12px' }}>{error}</div>}
          {success && <div className="success" style={{ marginBottom: '12px' }}>{success}</div>}

          {/* ── Terminal status overlay ── */}
          {checkoutId && terminalStatus && (
            <div style={{
              marginBottom: '16px', padding: '20px', borderRadius: '12px', textAlign: 'center',
              background: terminalStatus === 'COMPLETED'
                ? (darkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)')
                : terminalStatus === 'CANCELED' || terminalStatus === 'FAILED'
                  ? (darkMode ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)')
                  : (darkMode ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)'),
              border: `2px solid ${
                terminalStatus === 'COMPLETED' ? '#16a34a'
                : terminalStatus === 'CANCELED' || terminalStatus === 'FAILED' ? '#ef4444'
                : '#3b82f6'
              }`,
            }}>
              {(terminalStatus === 'PENDING' || terminalStatus === 'IN_PROGRESS') && (
                <>
                  <div style={{ fontWeight: 700, fontSize: '17px', color: darkMode ? '#fff' : '#111827', marginBottom: '6px' }}>
                    {terminalStatus === 'PENDING' ? 'Sent to terminal...' : 'Customer is paying...'}
                  </div>
                  <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                    Waiting for payment on the Square Terminal.
                    {terminalSentAt && (
                      <span> ({Math.round((Date.now() - terminalSentAt.getTime()) / 1000)}s ago)</span>
                    )}
                  </div>
                  <div style={{
                    marginTop: '12px', height: '4px', borderRadius: '2px',
                    background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: '2px',
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)',
                      backgroundSize: '200% 100%',
                      animation: 'squarePayShimmer 1.5s ease-in-out infinite',
                      width: '100%',
                    }} />
                  </div>
                  <style>{`@keyframes squarePayShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
                </>
              )}
              {terminalStatus === 'COMPLETED' && (
                <div style={{ fontWeight: 700, fontSize: '17px', color: '#16a34a' }}>Payment completed!</div>
              )}
              {(terminalStatus === 'CANCELED' || terminalStatus === 'FAILED') && (
                <div style={{ fontWeight: 700, fontSize: '17px', color: '#ef4444' }}>
                  {terminalStatus === 'FAILED' ? 'Payment failed' : 'Payment cancelled'}
                </div>
              )}
            </div>
          )}

          {/* Payment method selection */}
          {!checkoutId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Terminal option */}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '14px', borderRadius: '12px', cursor: 'pointer',
                background: paymentMethod === 'terminal'
                  ? (darkMode ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)')
                  : (darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                border: paymentMethod === 'terminal'
                  ? '2px solid #3b82f6'
                  : (darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'),
              }}>
                <input type="radio" name="payMethod" value="terminal"
                  checked={paymentMethod === 'terminal'}
                  onChange={() => { setPaymentMethod('terminal'); autoSentRef.current = false; }}
                  style={{ marginTop: '3px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: darkMode ? '#fff' : '#111827' }}>
                    Send to Terminal
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginTop: '4px' }}>
                    Customer pays on your Square Terminal with card or digital wallet.
                  </div>
                  {paymentMethod === 'terminal' && (
                    <div style={{ marginTop: '10px' }}>
                      {loadingDevices ? (
                        <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>Loading devices...</div>
                      ) : devices.length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#f59e0b' }}>No paired terminal found. Pair one in Settings first.</div>
                      ) : (
                        <>
                          <select
                            value={selectedDeviceId}
                            onChange={(e) => { setSelectedDeviceId(e.target.value); autoSentRef.current = false; }}
                            style={{
                              width: '100%', padding: '8px 10px', borderRadius: '8px', fontSize: '13px',
                              background: darkMode ? 'rgba(255,255,255,0.06)' : '#fff',
                              border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
                              color: darkMode ? '#fff' : '#111827',
                            }}
                          >
                            <option value="">Select device...</option>
                            {devices.map(device => (
                              <option key={device.id} value={device.id}>
                                {device.name || device.id}
                                {' '}
                                {['Online', 'AVAILABLE', 'ACTIVE', 'ACTIVE_READY'].includes(device.status) ? '(online)' : ''}
                              </option>
                            ))}
                          </select>
                          {selectedDeviceId && !processingPayment && (
                            <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '6px', fontWeight: 500 }}>
                              Sending to terminal automatically...
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </label>

              {/* Cash option */}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '14px', borderRadius: '12px', cursor: 'pointer',
                background: paymentMethod === 'cash'
                  ? (darkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)')
                  : (darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                border: paymentMethod === 'cash'
                  ? '2px solid #16a34a'
                  : (darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'),
              }}>
                <input type="radio" name="payMethod" value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={() => setPaymentMethod('cash')}
                  style={{ marginTop: '3px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: darkMode ? '#fff' : '#111827' }}>
                    Record Cash Payment
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginTop: '4px' }}>
                    Mark the order as paid with cash. Recorded in Square.
                  </div>
                </div>
              </label>

              {/* Already paid in Square POS */}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '14px', borderRadius: '12px', cursor: 'pointer',
                background: paymentMethod === 'already_paid'
                  ? (darkMode ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)')
                  : (darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                border: paymentMethod === 'already_paid'
                  ? '2px solid #f59e0b'
                  : (darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'),
              }}>
                <input type="radio" name="payMethod" value="already_paid"
                  checked={paymentMethod === 'already_paid'}
                  onChange={() => setPaymentMethod('already_paid')}
                  style={{ marginTop: '3px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: darkMode ? '#fff' : '#111827' }}>
                    Already paid in Square POS
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginTop: '4px' }}>
                    Payment was already processed on the Square Register / POS. Just mark this service as completed.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* ── Action buttons ── */}
          <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {!checkoutId && paymentMethod !== 'terminal' && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleProcessPayment}
                disabled={processingPayment}
                style={{
                  background: paymentMethod === 'cash'
                    ? 'linear-gradient(135deg, #16a34a, #15803d)'
                    : 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: '#fff', border: 'none', fontWeight: 600, fontSize: '15px',
                  padding: '10px 24px',
                }}
              >
                {processingPayment
                  ? 'Processing...'
                  : paymentMethod === 'cash'
                    ? 'Record Cash Payment'
                    : 'Mark as Completed'}
              </button>
            )}
            {!checkoutId && paymentMethod === 'terminal' && selectedDeviceId && !processingPayment && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { autoSentRef.current = true; handleProcessPayment(); }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  color: '#fff', border: 'none', fontWeight: 600, fontSize: '15px',
                  padding: '10px 24px',
                }}
              >
                Send to Terminal
              </button>
            )}
            {checkoutId && (terminalStatus === 'PENDING' || terminalStatus === 'IN_PROGRESS') && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                  setCheckoutId(null);
                  setTerminalStatus(null);
                  setProcessingPayment(false);
                }}
                style={{ fontWeight: 600 }}
              >
                Cancel &amp; choose another method
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={processingPayment && paymentMethod === 'terminal' && !!checkoutId}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SquarePaymentModal;
